import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../layout/header";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { _get } from "../../../libraries/fetch";
import { getAllowedBranchTabs } from "../../../libraries/auth";
import { getDefaultAccessibleRoute, isMobileAppBranch, MOBILE_CLIENTS_PATH } from "../../../libraries/privileges";
import LicenseValidity from "../components/licenseValidity";
import { getAllBranchesURL, getBranchURL, getUpdateLicenseRequestsByBranchURL } from "../../../libraries/apiUrl";
import { getBranchLogoUrl, getClientInitials } from "../../../libraries/branchLogo";
import { toClientStatusFilterParam } from "../../../libraries/licenseExpiry";
import { getActivityByName } from "../../../libraries/activityLogger";
import {
  extractPendingLicenseRequestKeys,
  getStoredPendingLicenseRequestKeys,
  isLicenseRequestNotificationCreator,
  isLicenseRequestNotificationRecipient,
  playArrivalNotificationTone,
  savePendingLicenseRequestKeys,
  showBrowserArrivalNotification
} from "../../../libraries/arrivalNotifications";
import Dashboard from "../components/Dashboard";
import Info from "../components/Info";
import UsersInfo from "../components/UsersInfo";
import ClientQueries from "../components/ClientQueries";
import OnboardingObject from "../components/OnboardingObject";
import OnboardingStatus from "../components/OnboardingStatus";
import AnyDesk from "../components/AnyDesk";
import ZoneManagement from "../components/ZoneManagement";
import Packages from "../components/Packages";
import Analytics from "../components/Analytics";
import UpdateLicense from "../components/UpdateLicense";
import DesktopCredential from "../components/DesktopCredential";
import ActivityLog from "../components/ActivityLog";
import SyncAllMembers from "../components/SyncAllMembers";

const LICENSE_REQUEST_POLL_INTERVAL_MS = 60 * 1000;

function Branch() {
  const location = useLocation();
  const navigate = useNavigate();
  const [branchId, setBranchId] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [clientsFilter, setClientsFilter] = useState("active");
  const [clientsSource, setClientsSource] = useState("web");
  const [currentBranch, setCurrentBranch] = useState({});
  const [daysLeft, setDaysLeft] = useState(0);
  const [activeRoute, setActiveRoute] = useState("Dashboard");
  const [pendingLicenseRequestCount, setPendingLicenseRequestCount] = useState(0);
  const [licenseNotification, setLicenseNotification] = useState("");
  const [logoBroken, setLogoBroken] = useState(false);
  const licenseNotificationTimerRef = useRef(null);
  const notificationPermissionAskedRef = useRef(false);
  const gymLogoUrl = useMemo(() => getBranchLogoUrl(currentBranch), [currentBranch]);
  const allowedBranchTabs = useMemo(() => getAllowedBranchTabs(), []);
  const allowedTabIds = useMemo(
    () => new Set(allowedBranchTabs.map((tab) => tab.id)),
    [allowedBranchTabs]
  );

  const openUpdateLicenseTab = useCallback(() => {
    if (allowedTabIds.has("UpdateLicense")) {
      setActiveRoute("UpdateLicense");
    }
  }, [allowedTabIds]);

  const showLicenseArrivalNotification = useCallback((detail) => {
    const generatedBy = detail && typeof detail === "object" ? detail.generatedBy : "";

    if (
      !isLicenseRequestNotificationRecipient(getActivityByName()) ||
      !isLicenseRequestNotificationCreator(generatedBy)
    ) {
      return;
    }

    setLicenseNotification("Hifsa Maryam created a license update request.");
    playArrivalNotificationTone();

    if (licenseNotificationTimerRef.current) {
      window.clearTimeout(licenseNotificationTimerRef.current);
    }

    licenseNotificationTimerRef.current = window.setTimeout(() => {
      setLicenseNotification("");
    }, 6000);

    showBrowserArrivalNotification({
      title: "License Update Request",
      body: "Hifsa Maryam created a license update request.",
      onClick: openUpdateLicenseTab,
      permissionAskedRef: notificationPermissionAskedRef
    });
  }, [openUpdateLicenseTab]);

  const syncPendingLicenseRequests = useCallback(
    (pendingKeys, count, { notifyIfNew = false } = {}) => {
      const nextKeys = Array.isArray(pendingKeys) ? pendingKeys.map(String) : [];
      const previousKeys = getStoredPendingLicenseRequestKeys(branchId);

      setPendingLicenseRequestCount(typeof count === "number" ? count : nextKeys.length);

      if (
        notifyIfNew &&
        Array.isArray(previousKeys) &&
        nextKeys.some((requestKey) => !previousKeys.includes(requestKey))
      ) {
        showLicenseArrivalNotification();
      }

      if (branchId) {
        savePendingLicenseRequestKeys(branchId, nextKeys);
      }
    },
    [branchId, showLicenseArrivalNotification]
  );

  const handlePendingLicenseRequestsChange = useCallback(
    (pendingKeys, count) => {
      syncPendingLicenseRequests(pendingKeys, count, { notifyIfNew: false });
    },
    [syncPendingLicenseRequests]
  );

  useEffect(() => {
    return () => {
      if (licenseNotificationTimerRef.current) {
        window.clearTimeout(licenseNotificationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!allowedTabIds.has("UpdateLicense") || !branchId) {
      setPendingLicenseRequestCount(0);
      return undefined;
    }

    let isMounted = true;

    const fetchPendingLicenseRequests = async () => {
      try {
        const response = await _get(getUpdateLicenseRequestsByBranchURL(branchId), {
          useCache: false
        });

        if (response?.success === false || response?.status === false) {
          return;
        }

        const pendingKeys = extractPendingLicenseRequestKeys(response);

        if (!isMounted) {
          return;
        }

        syncPendingLicenseRequests(pendingKeys, pendingKeys.length, { notifyIfNew: true });
      } catch {
        // Keep the current badge if a background polling request fails.
      }
    };

    fetchPendingLicenseRequests();
    const intervalId = window.setInterval(
      fetchPendingLicenseRequests,
      LICENSE_REQUEST_POLL_INTERVAL_MS
    );

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [allowedTabIds, branchId, syncPendingLicenseRequests]);
  useEffect(() => {
    if (allowedBranchTabs.length === 0) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
    }
  }, [allowedBranchTabs.length, navigate]);

  useEffect(() => {
    if (!allowedTabIds.has(activeRoute)) {
      setActiveRoute(allowedBranchTabs[0]?.id || "Dashboard");
    }
  }, [activeRoute, allowedBranchTabs, allowedTabIds]);

  useEffect(() => {
    const stateBranchId = location.state?.branchId;
    const pathBranchId = location.pathname.split("/").pop();
    const resolvedBranchId = stateBranchId || pathBranchId || "";
    setBranchId(resolvedBranchId);

    const stateActiveRoute = location.state?.activeRoute;
    if (stateActiveRoute && allowedTabIds.has(stateActiveRoute)) {
      setActiveRoute(stateActiveRoute);
    }

    const stateBranchIds = Array.isArray(location.state?.branchIds) ? location.state.branchIds : [];
    setBranchIds(stateBranchIds);

    if (location.state?.clientsFilter) {
      setClientsFilter(location.state.clientsFilter);
    }

    if (location.state?.clientsSource) {
      setClientsSource(location.state.clientsSource);
    }

    if (stateBranchIds.length > 0) {
      const stateIndex = typeof location.state?.currentIndex === "number" ? location.state.currentIndex : -1;
      const resolvedIndex = stateIndex >= 0 ? stateIndex : stateBranchIds.indexOf(resolvedBranchId);
      setCurrentIndex(resolvedIndex);
    } else {
      setCurrentIndex(-1);
    }
  }, [allowedTabIds, location.pathname, location.state]);

  const handleClientNavigation = (direction) => {
    if (branchIds.length === 0 || currentIndex < 0) return;

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= branchIds.length) return;

    const nextBranchId = branchIds[nextIndex];
    navigate("/branches/" + nextBranchId, {
      state: {
        branchId: nextBranchId,
        branchIds,
        currentIndex: nextIndex,
        clientsFilter,
        clientsSource
      }
    });
  };

  useEffect(() => {
    if (branchId === undefined || branchId === null || branchId === "") {
      return;
    }

    setLogoBroken(false);

    _get(getBranchURL(branchId))
      .then((data) => {
        const branch = data?._id || data?.id
          ? data
          : (data?.response || data?.data || data);
        setCurrentBranch(branch && typeof branch === "object" ? branch : {});
        setLogoBroken(false);
      })
      .catch(() => {});
  }, [branchId]);

  useEffect(() => {
    if (!branchId || branchIds.length > 0) {
      return;
    }

    _get(getAllBranchesURL())
      .then((data) => {
        const branches = Array.isArray(data)
          ? data
          : Array.isArray(data?.response)
            ? data.response
            : Array.isArray(data?.data)
              ? data.data
              : [];

        const ids = branches
          .filter((item) => item && item._id)
          .filter((item) => (
            clientsSource === "mobile"
              ? isMobileAppBranch(item)
              : !isMobileAppBranch(item)
          ))
          .map((item) => item._id);

        setBranchIds(ids);
        setCurrentIndex(ids.indexOf(branchId));
      })
      .catch(() => {
        setBranchIds([]);
        setCurrentIndex(-1);
      });
  }, [branchId, branchIds.length, clientsSource]);


  return (
    <div className="main">
      <Header />
      <div className="branch-client-nav" style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "12px 20px 0", flexWrap: "wrap" }}>
        <button
          onClick={() => handleClientNavigation(-1)}
          disabled={currentIndex <= 0}
          style={{
            padding: "8px 14px",
            borderRadius: "6px",
            border: "1px solid #444",
            background: currentIndex <= 0 ? "#3b3f47" : "#187718",
            color: "#fff",
            cursor: currentIndex <= 0 ? "not-allowed" : "pointer",
            opacity: currentIndex <= 0 ? 0.6 : 1,
            fontWeight: 600
          }}
        >
          Previous
        </button>
        <button
          onClick={() => handleClientNavigation(1)}
          disabled={currentIndex < 0 || currentIndex >= branchIds.length - 1}
          style={{
            padding: "8px 14px",
            borderRadius: "6px",
            border: "1px solid #444",
            background: currentIndex < 0 || currentIndex >= branchIds.length - 1 ? "#3b3f47" : "#187718",
            color: "#fff",
            cursor: currentIndex < 0 || currentIndex >= branchIds.length - 1 ? "not-allowed" : "pointer",
            opacity: currentIndex < 0 || currentIndex >= branchIds.length - 1 ? 0.6 : 1,
            fontWeight: 600
          }}
        >
          Next
        </button>
      </div>
      <div className="dashboardMain">
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
          <button
            type="button"
            onClick={() => {
              const listPath = clientsSource === "mobile" ? MOBILE_CLIENTS_PATH : "/branches";
              navigate(`${listPath}?filter=${encodeURIComponent(toClientStatusFilterParam(clientsFilter))}`);
            }}
            style={{
              padding: "8px 14px",
              borderRadius: "6px",
              border: "1px solid #444",
              background: "#187718",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600,
              whiteSpace: "nowrap"
            }}
          >
            Back
          </button>
          <div className="branchInfo">
              <h3>Branch Name : </h3>
              <div className="branch-name-row">
                <h1>{currentBranch.name}</h1>
                {currentBranch.name && (
                  <div className="branch-gym-logo">
                    {gymLogoUrl && !logoBroken ? (
                      <img
                        src={gymLogoUrl}
                        alt={`${currentBranch.name} logo`}
                        className="branch-gym-logo-image"
                        onError={() => setLogoBroken(true)}
                      />
                    ) : (
                      <span className="branch-gym-logo-initials">
                        {getClientInitials(currentBranch.name)}
                      </span>
                    )}
                  </div>
                )}
              </div>
          </div>
        </div>
        <div className="branchInfo">
          <h3>Gym Status : </h3>
          <h1>{currentBranch.status}</h1>
        </div>
        <div className="branchInfo">
          <h3>License Status : </h3>
          <h1 style={(daysLeft < 0) ? { color: '#d51717' } : {}}>{(daysLeft < 0) ? "Expired" : "Active"}</h1>
        </div>
        <div className="branchInfo">
          <h3>Days Remaining :</h3>
          <h1>{daysLeft}</h1>
          <div style={{display:"none"}}>
            {currentBranch.company && currentBranch._id && (
              <LicenseValidity companyId={currentBranch.company} branchId={currentBranch._id} daysOutPut={(value) => setDaysLeft(value)} />
            )}
          </div>
        </div>
      </div>
      <div className="branchMenu">
        {allowedBranchTabs.map((tab) => (
          <button
            key={tab.id}
            className={activeRoute === tab.id ? "active" : ""}
            onClick={() => setActiveRoute(tab.id)}
            style={
              tab.id === "UpdateLicense" && pendingLicenseRequestCount > 0
                ? { display: "inline-flex", alignItems: "center", gap: "0" }
                : undefined
            }
          >
            {tab.label}
            {tab.id === "UpdateLicense" && pendingLicenseRequestCount > 0 && (
              <span
                style={licenseRequestBadgeStyle}
                aria-label={`${pendingLicenseRequestCount} pending license update requests`}
              >
                {pendingLicenseRequestCount > 99 ? "99+" : pendingLicenseRequestCount}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="contentArea">
        {activeRoute === "Dashboard" && allowedTabIds.has("Dashboard") && (
          <Dashboard
            branchId={currentBranch._id || branchId}
            companyId={currentBranch.company}
            gymPackage={currentBranch.gymPackage}
          />
        )}
        {activeRoute === "OnboardingObject" && allowedTabIds.has("OnboardingObject") && (
          <OnboardingObject
            branchId={currentBranch._id}
            branchName={currentBranch.name}
            adminEmail={currentBranch.contactPersonEmail || currentBranch.email}
            adminPhone={currentBranch.phone || currentBranch.contactNumber || currentBranch.mobile}
          />
        )}
        {activeRoute === "OnboardingStatus" && allowedTabIds.has("OnboardingStatus") && (
          <OnboardingStatus
            branchId={currentBranch._id}
            branchName={currentBranch.name}
            companyId={currentBranch.company || currentBranch.companyId}
            branch={currentBranch}
          />
        )}
        {activeRoute === "Info" && allowedTabIds.has("Info") && <Info companyId={currentBranch.company || currentBranch.companyId} branchId={currentBranch._id || branchId} Name={currentBranch.name} licenseKey={currentBranch.license?.key || currentBranch.license?.licenseKey || "Not available"} phone={currentBranch.phone} email={currentBranch.contactPersonEmail} address={currentBranch.address} branch={currentBranch} />}
        {activeRoute === "UsersInfo" && allowedTabIds.has("UsersInfo") && <UsersInfo branchId={currentBranch._id} companyId={currentBranch.company} />}
        {activeRoute === "ClientsQuerries" && allowedTabIds.has("ClientsQuerries") && (
          <ClientQueries companyId={currentBranch.company} branchId={currentBranch._id || branchId} />
        )}
        {activeRoute === "AnyDesk" && allowedTabIds.has("AnyDesk") && (
          <AnyDesk
            branchId={currentBranch._id}
            companyId={currentBranch.company}
            branchName={currentBranch.name}
          />
        )}
        {activeRoute === "ZoneManagement" && allowedTabIds.has("ZoneManagement") && (
          <ZoneManagement
            branchId={currentBranch._id || branchId}
            companyId={currentBranch.company || currentBranch.companyId}
          />
        )}
        {activeRoute === "Packages" && allowedTabIds.has("Packages") && <Packages companyId={currentBranch.company} branchId={currentBranch._id} /> }
        {activeRoute === "Analytics" && allowedTabIds.has("Analytics") && <Analytics branchId={currentBranch._id} /> }
        {activeRoute === "UpdateLicense" && allowedTabIds.has("UpdateLicense") && (
          <UpdateLicense
            companyId={
              currentBranch.company?._id ||
              currentBranch.company?.id ||
              currentBranch.company ||
              currentBranch.companyId
            }
            branchId={currentBranch._id || currentBranch.id || branchId}
            branchName={currentBranch.name}
            branch={currentBranch}
            license={currentBranch.license}
            onLicenseRequestGenerated={showLicenseArrivalNotification}
            onPendingLicenseRequestsChange={handlePendingLicenseRequestsChange}
          />
        )}
        {activeRoute === "DesktopCredential" && allowedTabIds.has("DesktopCredential") && (
          <DesktopCredential
            branchId={currentBranch._id}
            branch={currentBranch}
          />
        )}
        {activeRoute === "ActivityLog" && allowedTabIds.has("ActivityLog") && <ActivityLog companyId={currentBranch.company} branchId={currentBranch._id} /> }
        {activeRoute === "SyncAll" && allowedTabIds.has("SyncAll") && <SyncAllMembers branchId={currentBranch._id} branchName={currentBranch.name} /> }
      </div>
      {licenseNotification && (
        <div
          className="query-toast"
          role="button"
          tabIndex={0}
          aria-live="polite"
          onClick={openUpdateLicenseTab}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openUpdateLicenseTab();
            }
          }}
        >
          <strong style={licensePopupHeadingStyle}>License Update Request</strong>
          <span style={licensePopupTextStyle}>{licenseNotification}</span>
        </div>
      )}
    </div>
  );
}

const licenseRequestBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "22px",
  height: "22px",
  marginLeft: "8px",
  padding: "0 6px",
  borderRadius: "999px",
  backgroundColor: "#ef4444",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 800,
  lineHeight: 1
};

const licensePopupHeadingStyle = {
  color: "#4ade80",
  fontSize: "21px",
  fontWeight: 800,
  lineHeight: 1.2
};

const licensePopupTextStyle = {
  fontSize: "17px",
  fontWeight: 600,
  lineHeight: 1.35
};

export default Branch;
